import { useEffect, useState } from 'react'
import { Check, ExternalLink } from 'lucide-react'
import { PageHeader, Panel, Button, Chip, Modal, FormRow, Field, Textarea, Switch, EmptyState, ConfirmDialog, DefinitionList, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { api, post, patch } from '../lib/api'
import { money, dateShort, ago } from '../lib/format'
import { Icon } from '../components/Icon'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { TermsText } from './client/Signup'

type App = {
  id: string; name: string; legal_name: string | null; entity_type: string | null; entity_state: string | null; trade: string | null
  roc_license: string | null; roc_verified_at: string | null; roc_not_needed: boolean; roc_verified_by_name: string | null
  website: string | null; facebook_page: string | null; mailing_address: string | null; submitted_at: string | null; signup_started_at: string
  monthly_amount_cents: number | null; zone: { name: string; status: string } | null; checklist: { what: string; done: boolean }[]
  accepted: { version: string; typed_name: string; title: string | null; accepted_at: string } | null; card: { brand: string | null; last4: string | null } | null
  question: { question: string; created_at: string } | null; account: { name: string; email: string; phone: string | null; email_confirmed: boolean } | null
}
type TermsInfo = {
  legal_name: string | null; state_of_formation: string | null; notice_email: string | null; signup_open: boolean; missing: string[]; join_url: string
  closed_reason: string | null; versions: { id: string; version: string; effective_at: string; published_at: string; accepted: number }[]
}
// The Arizona Registrar of Contractors' public license search.
const ROC_SEARCH = 'https://roc.az.gov/contractor-search'

/**
 * Sign-ups (Business Terms 2.8 and Article 16): the company's own details that
 * fill the Terms, sign-up open or closed, the versions, and every business
 * waiting with its checklist. Approve starts leads and charges the first tab;
 * Ask emails a question; Decline charges nothing and removes the card.
 */
export default function Applications() {
  const a = useApi<{ applications: App[] }>('/api/applications')
  const t = useApi<TermsInfo>('/api/terms')
  return (
    <>
      <PageHeader title="Sign-ups" subtitle="Businesses that signed up on the website, and the Terms they sign." />
      <div className="stack">
        {t.error && <ErrorBlock error={t.error} />}
        {t.data && <TermsSettings t={t.data} onSaved={t.reload} />}
        {a.error ? <ErrorBlock error={a.error} /> : !a.data ? <LoadingBlock /> : a.data.applications.length === 0
          ? <Panel><EmptyState title="Nobody waiting" body="A business that signs up appears here once it starts, and is ready to approve once every box is ticked." /></Panel>
          : a.data.applications.map((x) => <AppCard key={x.id} app={x} onDone={a.reload} />)}
        {t.data && <Versions t={t.data} onSaved={t.reload} />}
      </div>
    </>
  )
}

function TermsSettings({ t, onSaved }: { t: TermsInfo; onSaved: () => void }) {
  const toast = useToast()
  const [f, setF] = useState({ legal_name: t.legal_name || '', state_of_formation: t.state_of_formation || '', notice_email: t.notice_email || '' })
  useEffect(() => setF({ legal_name: t.legal_name || '', state_of_formation: t.state_of_formation || '', notice_email: t.notice_email || '' }), [t])
  const save = async (extra: Record<string, unknown> = {}) => {
    try { await patch('/api/terms', { ...f, ...extra }); toast('Saved', 'pos'); onSaved() } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Sign-up on the website</h2>
        <Switch checked={t.signup_open} onChange={(e) => save({ signup_open: e.target.checked })} label={t.signup_open ? 'Open' : 'Closed'} />
      </div>
      {t.missing.length > 0 && <p className="muted" style={{ marginTop: 0 }}>The Terms have three blanks for your company's details. Sign-up cannot open until they are filled: still missing {t.missing.join(', ')}.</p>}
      <div className="form-grid">
        <Field label="Exact legal name" id="t-legal" placeholder="Handpicked Network LLC" value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} />
        <Field label="State of formation" id="t-state" placeholder="Arizona" value={f.state_of_formation} onChange={(e) => setF({ ...f, state_of_formation: e.target.value })} />
        <Field label="Notice email" id="t-notice" type="email" value={f.notice_email} onChange={(e) => setF({ ...f, notice_email: e.target.value })} />
      </div>
      <div className="spread wrap-gap" style={{ marginTop: 'var(--s-5)', alignItems: 'center' }}>
        <Button variant="secondary" onClick={() => save()}>Save details</Button>
        {t.signup_open && <span className="muted">Sign-up page: <a href={t.join_url} target="_blank" rel="noopener noreferrer">{t.join_url}</a></span>}
      </div>
    </Panel>
  )
}

function AppCard({ app, onDone }: { app: App; onDone: () => void }) {
  const toast = useToast()
  const [acting, setActing] = useState<'ask' | 'decline' | 'approve' | null>(null)
  const [text, setText] = useState('')
  const roc = async (notNeeded: boolean) => {
    try { await post(`/api/applications/${app.id}/roc`, { not_needed: notNeeded }); toast(notNeeded ? 'Marked: no license needed' : 'License marked checked', 'pos'); onDone() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const act = async () => {
    try {
      if (acting === 'approve') {
        const r = await post<{ tab: string | null }>(`/api/applications/${app.id}/approve`, {})
        toast(r.tab === 'failed' ? 'Approved, but the first tab did not go through: leads are paused until it is paid' : 'Approved. Leads start now and the first tab was charged.', r.tab === 'failed' ? 'neg' : 'pos')
      } else {
        await post(`/api/applications/${app.id}/${acting}`, acting === 'ask' ? { question: text } : { reason: text })
        toast(acting === 'ask' ? 'Question emailed' : 'Declined. Nothing was charged and the card was removed.', 'pos')
      }
      setActing(null); setText(''); onDone()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  const rocDone = Boolean(app.roc_verified_at || app.roc_not_needed)
  const ready = app.checklist.every((c) => c.done) && rocDone
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">{app.name}</h2>
        <Chip tone={app.submitted_at ? (ready ? 'pos' : 'warn') : undefined} dot>{!app.submitted_at ? 'Still signing up' : ready ? 'Ready to approve' : 'Needs a check'}</Chip>
      </div>
      <div className="two-col">
        <DefinitionList items={[
          { label: 'Legal name', value: [app.legal_name, app.entity_state].filter(Boolean).join(', ') || 'Not yet' },
          { label: 'Trade', value: app.trade || '' },
          { label: 'Zone asked for', value: app.zone ? app.zone.name : 'None yet' },
          { label: 'Tab', value: app.monthly_amount_cents ? `${money(app.monthly_amount_cents)} a month` : 'Not chosen' },
          { label: 'Signed', value: app.accepted ? `v${app.accepted.version} by ${app.accepted.typed_name}${app.accepted.title ? `, ${app.accepted.title}` : ''}` : 'Not yet' },
          { label: 'Card', value: app.card ? `${app.card.brand} ending ${app.card.last4}` : 'Not yet' },
          { label: 'Contact', value: app.account ? `${app.account.name}, ${app.account.email}${app.account.phone ? `, ${app.account.phone}` : ''}` : '' },
          { label: 'Real business', value: <>{app.website && <a href={app.website} target="_blank" rel="noopener noreferrer">Website <Icon of={ExternalLink} size={13} /></a>} {app.facebook_page && <a href={app.facebook_page} target="_blank" rel="noopener noreferrer">Facebook <Icon of={ExternalLink} size={13} /></a>}</> },
        ]} />
        <div className="stack-tight">
          <ul className="join-check">
            {app.checklist.map((c) => <li key={c.what} className={c.done ? 'is-done' : ''}>{c.done ? <Icon of={Check} size={14} /> : <span className="join-dot-empty" />}{c.what}</li>)}
            <li className={rocDone ? 'is-done' : ''}>{rocDone ? <Icon of={Check} size={14} /> : <span className="join-dot-empty" />}ROC license {app.roc_not_needed ? 'not needed' : app.roc_verified_at ? `checked ${dateShort(app.roc_verified_at)}${app.roc_verified_by_name ? ` by ${app.roc_verified_by_name}` : ''}` : 'checked by a person'}</li>
          </ul>
          {!rocDone && (
            <div className="stack-tight">
              <span className="muted">License {app.roc_license || 'not given'}. <a href={ROC_SEARCH} target="_blank" rel="noopener noreferrer">Search the ROC <Icon of={ExternalLink} size={13} /></a></span>
              <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                {app.roc_license && <Button variant="secondary" onClick={() => roc(false)}>I checked it: active</Button>}
                <Button variant="ghost" onClick={() => roc(true)}>Not needed for this trade</Button>
              </div>
            </div>
          )}
          {app.question && <p className="muted" style={{ margin: 0 }}>Asked {ago(app.question.created_at)}: {app.question.question}</p>}
        </div>
      </div>
      <div className="row-tight" style={{ marginTop: 'var(--s-5)', flexWrap: 'wrap' }}>
        <Button onClick={() => setActing('approve')} disabled={!ready}>Approve</Button>
        <Button variant="secondary" onClick={() => setActing('ask')}>Ask a question</Button>
        <Button variant="ghost" onClick={() => setActing('decline')}>Decline</Button>
      </div>
      {acting === 'approve' && (
        <ConfirmDialog title={`Approve ${app.name}?`} confirmLabel="Approve and start leads"
          body={`Their zone (${app.zone?.name || 'none'}) is confirmed, leads start now, and the first monthly tab of ${money(app.monthly_amount_cents || 0)} is charged to their card. They are emailed.`}
          onConfirm={act} onCancel={() => setActing(null)} />
      )}
      {(acting === 'ask' || acting === 'decline') && (
        <Modal title={acting === 'ask' ? `Ask ${app.name} a question` : `Decline ${app.name}`} onClose={() => setActing(null)}
          footer={<><Button variant="secondary" onClick={() => setActing(null)}>Cancel</Button><Button onClick={act}>{acting === 'ask' ? 'Email the question' : 'Decline'}</Button></>}>
          <p style={{ marginTop: 0 }}>{acting === 'ask' ? 'They are emailed and can reply. Their sign-up waits.' : 'Nothing is charged, their card is removed, and they are emailed this reason.'}</p>
          <FormRow label={acting === 'ask' ? 'Question' : 'Reason (they see this)'} htmlFor="ap-text"><Textarea id="ap-text" rows={3} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} /></FormRow>
        </Modal>
      )}
    </Panel>
  )
}

function Versions({ t, onSaved }: { t: TermsInfo; onSaved: () => void }) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [f, setF] = useState({ version: '', body: '', effective: '' })
  const minDay = new Date(Date.now() + 8 * 86400000).toISOString().slice(0, 10)
  const publish = async () => {
    try {
      const r = await post<{ told: number }>('/api/terms/versions', { version: f.version, body: f.body, effective_at: new Date(`${f.effective}T00:00:00-07:00`).toISOString() })
      toast(`Published. ${r.told} business${r.told === 1 ? '' : 'es'} emailed.`, 'pos'); setOpen(false); onSaved()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Versions of the Terms</h2><Button variant="secondary" onClick={() => setOpen(true)}>Publish a new version</Button></div>
      <div className="bill-list">
        {t.versions.map((v) => (
          <div key={v.id} className="bill-row">
            <strong>Version {v.version}</strong>
            <span className="muted">from {dateShort(v.effective_at)}</span>
            <span className="muted">{v.accepted} signed</span>
          </div>
        ))}
      </div>
      {open && (
        <Modal title="Publish a new version" onClose={() => setOpen(false)} footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={publish}>Publish and email every business</Button></>}>
          <p style={{ marginTop: 0 }}>It takes effect at least 7 days out (Business Terms 16.5). Every approved business is emailed now and asked to agree at its first sign-in on or after that day; until then the version it signed applies. Keep {'{{legal_name}}'}, {'{{state_of_formation}}'} and {'{{notice_email}}'} where your details go.</p>
          <div className="form-grid">
            <Field label="Version" id="v-ver" placeholder="1.4" value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} />
            <Field label="Takes effect" id="v-eff" type="date" min={minDay} value={f.effective} onChange={(e) => setF({ ...f, effective: e.target.value })} />
          </div>
          <FormRow label="The full text" htmlFor="v-body"><Textarea id="v-body" rows={12} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></FormRow>
        </Modal>
      )}
    </Panel>
  )
}

type Agreement = {
  acceptances: { id: string; version: string; source: string; accepted_local: string; typed_name: string; title: string | null; email: string; ip: string | null; user_agent: string | null; text_sha256: string; emailed_at: string | null }[]
  card_authorizations: { id: string; consented_at: string; tab_amount_cents: number; card_brand: string | null; card_last4: string | null; ip: string | null }[]
}

/** On a client's page: every acceptance of the Terms and every card authorization, with the exact text. */
export function ClientAgreementRecords({ clientId }: { clientId: string }) {
  const a = useApi<Agreement>(`/api/clients/${clientId}/agreement`)
  const [text, setText] = useState<string | null>(null)
  if (!a.data || (!a.data.acceptances.length && !a.data.card_authorizations.length)) return null
  const view = async (id: string) => {
    setText((await api<{ text: string }>(`/api/clients/${clientId}/agreement/${id}/text`)).text)
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Agreement</h2></div>
      <div className="bill-list">
        {a.data.acceptances.map((x) => (
          <div key={x.id} className="bill-row">
            <div><strong>Terms v{x.version}</strong><div className="muted" style={{ fontSize: 13 }}>{x.typed_name}{x.title ? `, ${x.title}` : ''} · {x.email} · {x.accepted_local}{x.ip ? ` · IP ${x.ip}` : ''}{x.source === 'update' ? ' · update' : ''}</div></div>
            <span className="muted" title={x.text_sha256}>{x.emailed_at ? 'Copy emailed' : ''}</span>
            <Button variant="ghost" onClick={() => view(x.id)}>Exact text</Button>
          </div>
        ))}
        {a.data.card_authorizations.map((c) => (
          <div key={c.id} className="bill-row">
            <div><strong>Card authorization</strong><div className="muted" style={{ fontSize: 13 }}>{dateShort(c.consented_at)} · tab {money(c.tab_amount_cents)}{c.card_last4 ? ` · ${c.card_brand} ending ${c.card_last4}` : ' · card not saved yet'}</div></div>
            <span />
            <span />
          </div>
        ))}
      </div>
      {text && <Modal title="Exactly as shown" onClose={() => setText(null)} footer={<Button variant="secondary" onClick={() => setText(null)}>Close</Button>}><TermsText text={text} /></Modal>}
    </Panel>
  )
}
